import { addMonths, isValid, parse, parseISO, startOfDay } from 'date-fns';

export function getFormattedQualificationRecords(records, currentDate = new Date()) {
  return records.map((r) => ({
    id: r._id,
    qualificationId: r?.qualificationId || '',
    employeeKeyPayId: r?.employeeKeyPayId || '',
    employeeId: r?.employeeId || '',
    name: r?.name || '',
    employeeEmail: r?.employeeEmail || '',
    employeeDisplayName: r?.employeeDisplayName || '',
    qualificationUrl: r?.qualificationUrl || '',
    status: getQualificationStatus(r?.status, r?.expiryDate, r?.issueDate, r?.referenceNumber, currentDate),
    expiryDate: r?.expiryDate || null,
    issueDate: r?.issueDate || null,
    referenceNumber: r?.referenceNumber,
    documentId: r?.documentId,
    employeeStatus: r.portalAccess ? r.portalAccess : false,
    employeeMandatoryQualifications: r.mandatoryQualifications || []
  }));
}

//converting first word to capital
function firstWordCaps(status) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function parseQualificationDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  if (typeof value === 'string') {
    const isoDate = parseISO(value);
    if (isValid(isoDate)) return isoDate;

    const australianDate = parse(value, 'dd/MM/yyyy', new Date());
    return isValid(australianDate) ? australianDate : null;
  }

  const date = new Date(value);
  return isValid(date) ? date : null;
}

export function getQualificationStatus(status, expiryDate, issueDate, referenceNumber, currentDate = new Date()) {
  const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : '';
  const expiry = parseQualificationDate(expiryDate);

  if (expiry) {
    if (startOfDay(expiry) < startOfDay(currentDate)) return 'Expired';
    // An old Expired value is stale when the supplied expiry date is still current.
    if (normalizedStatus === 'expired') return 'Accepted';
  }

  // Qualifications without an expiry date never expire. A recorded issue date
  // or reference number confirms that an old Expired value should be accepted.
  if (!expiryDate && (issueDate || referenceNumber) && normalizedStatus === 'expired') {
    return 'Accepted';
  }

  return status ? firstWordCaps(status) : '';
}

//validate qualification record
//Superannuation,  Redundancy, Long Service Leave won't have expiry and issue date
export function isQualificationDataValid(qualificationData) {
  const { employeeKeyPayId, qualificationId, name, referenceNumber, issueDate, expiryDate } = qualificationData;

  //check required field
  if (!employeeKeyPayId || !qualificationId || !name || !referenceNumber) {
    return false;
  }

  //check that at least one date exists, if qual must have
  if (name !== 'Superannuation' && name !== 'Long Service Leave' && name !== 'Redundancy') {
    if (!issueDate && !expiryDate) {
      return false;
    }
  }

  return true;
}

//data needs to follow below pattern: as it will be sent direct to keypay from api
// {
//       "qualificationId": 26371,
//       "name": "OH&S (White Card)",
//       "expiryDate": null,
//       "issueDate": "2041-09-28T00:00:00",
//       "documents": [
//           {
//               "id": 1290977,
//               "name": "qualification.jpeg",
//               "dateCreated": "2025-11-17T00:34:12",
//               "visible": false
//           }
//       ],
//       "referenceNumber": "12378685"
//   },

export function validExpiryDate(expiryDate) {
  const currentDate = new Date();
  const expiryDateObj = new Date(expiryDate);

  if (expiryDateObj < currentDate) {
    return false;
  }
  return true;
}

/**
 * format customer payload
 * while updating customer it must contains sparse true, id and SyncToken
 */
//if any of this column's value is clicked, then can be inline editing
export const QUALIFICATION_INLINE_COLUMNS = [
  'expiryDate',
  'issueDate',
  'referenceNumber',
  'employeeMandatoryQualifications'
];

export function getMandatoryQualificationValue(row) {
  const value = row?.employeeMandatoryQualifications;
  if (typeof value === 'boolean') return value;
  return Array.isArray(value) ? value.includes(row?.name) : false;
}
export const getQualificationStatusColor = (
  status,
  expiryDate,
  issueDate,
  referenceNumber,
  currentDate = new Date()
) => {
  const effectiveStatus = getQualificationStatus(status, expiryDate, issueDate, referenceNumber, currentDate);
  const normalizedStatus = effectiveStatus.toLowerCase();
  const date = parseQualificationDate(expiryDate);

  if (date) {
    const today = startOfDay(currentDate);
    const expiryDay = startOfDay(date);

    if (expiryDay < today) {
      return 'red';
    }

    if (expiryDay <= addMonths(today, 1)) {
      if (normalizedStatus === 'accepted' || normalizedStatus === 'pending') {
        return 'yellow';
      }
    }
  }

  if (normalizedStatus === 'expired') {
    return 'red';
  }

  if (normalizedStatus === 'pending') {
    return 'yellow';
  }

  if (normalizedStatus === 'accepted') {
    return 'green';
  }

  return 'red';
};

export const statusColorStyles = {
  yellow: '#ca8a04',
  red: '#dc2626',
  green: '#16a34a'
};

export const statusTextClasses = {
  yellow: 'tw-text-yellow-600',
  red: 'tw-text-red-600',
  green: 'tw-text-green-600'
};
